
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";

type Activity = Tables<"activities"> & {
  activity_categories?: {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
  } | null;
};

type ActivityCategory = Tables<"activity_categories">;

interface UseActivitiesDataProps {
  searchTerm: string;
  selectedCategory: string;
  selectedDifficulty: string;
  selectedAgeGroup: string;
}

export const useActivitiesData = ({
  searchTerm,
  selectedCategory,
  selectedDifficulty,
  selectedAgeGroup,
}: UseActivitiesDataProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [categories, setCategories] = useState<ActivityCategory[]>([]);
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [difficultyLevels, setDifficultyLevels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch activities with categories
        const { data: activitiesData, error: activitiesError } = await supabase
          .from("activities")
          .select(`
            *,
            activity_categories (
              id,
              name,
              color,
              icon
            )
          `)
          .eq("status", "published")
          .order("created_at", { ascending: false });

        if (activitiesError) throw activitiesError;

        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from("activity_categories")
          .select("*")
          .eq("status", "published")
          .order("display_order", { ascending: true });

        if (categoriesError) throw categoriesError;

        setActivities(activitiesData || []);
        setCategories(categoriesData || []);

        // Extract unique age groups and difficulty levels from activities
        const uniqueAgeGroups = [...new Set(
          (activitiesData || [])
            .map(activity => activity.age_group)
            .filter(Boolean)
        )].sort();

        const uniqueDifficultyLevels = [...new Set(
          (activitiesData || [])
            .map(activity => activity.difficulty)
            .filter(Boolean)
        )].sort();

        setAgeGroups(uniqueAgeGroups);
        setDifficultyLevels(uniqueDifficultyLevels);
      } catch (error: any) {
        console.error("Error fetching activities:", error);
        toast({
          title: "Error",
          description: "Failed to load activities. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  // Filter activities based on search and filters
  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          activity.title.toLowerCase().includes(searchLower) ||
          activity.description?.toLowerCase().includes(searchLower) ||
          activity.activity_categories?.name.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Category filter
      if (selectedCategory && activity.category_id !== selectedCategory) {
        return false;
      }

      // Difficulty filter
      if (selectedDifficulty && activity.difficulty !== selectedDifficulty) {
        return false;
      }

      // Age group filter
      if (selectedAgeGroup && activity.age_group !== selectedAgeGroup) {
        return false;
      }

      return true;
    });
  }, [activities, searchTerm, selectedCategory, selectedDifficulty, selectedAgeGroup]);

  const stats = useMemo(() => ({
    totalActivities: activities.length,
    publishedActivities: activities.length,
    categoriesCount: categories.length,
  }), [activities.length, categories.length]);

  return {
    activities: filteredActivities,
    categories,
    ageGroups,
    difficultyLevels,
    loading,
    stats,
  };
};
